import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useClient } from "../../contexts/ClientContext";
import {
  getChangePasswordErrorKey,
  validateChangePasswordForm,
} from "./helpers";

function ChangePasswordForm() {
  const { t } = useTranslation();
  const { updatePassword } = useClient();
  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errorKey, setErrorKey] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorKey("");
    setIsSuccess(false);

    const validationErrorKey = validateChangePasswordForm(values);
    if (validationErrorKey) {
      setErrorKey(validationErrorKey);
      return;
    }

    setIsSubmitting(true);
    const response = await updatePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
    setIsSubmitting(false);

    if (!response.success) {
      setErrorKey(getChangePasswordErrorKey(response.error));
      return;
    }

    setValues({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    setIsSuccess(true);
  };

  return (
    <Stack component="form" spacing={2} onSubmit={handleSubmit}>
      <TextField
        required
        type={showCurrentPassword ? "text" : "password"}
        label={t("auth.currentPasswordLabel")}
        value={values.currentPassword}
        onChange={(event) =>
          setValues((prev) => ({
            ...prev,
            currentPassword: event.target.value,
          }))
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t("auth.togglePasswordVisibility")}
                onClick={() => setShowCurrentPassword((prev) => !prev)}
                edge="end"
              >
                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        required
        type={showNewPassword ? "text" : "password"}
        label={t("auth.newPasswordLabel")}
        helperText={t("auth.passwordMinLength")}
        value={values.newPassword}
        onChange={(event) =>
          setValues((prev) => ({ ...prev, newPassword: event.target.value }))
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t("auth.togglePasswordVisibility")}
                onClick={() => setShowNewPassword((prev) => !prev)}
                edge="end"
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        required
        type={showConfirmNewPassword ? "text" : "password"}
        label={t("auth.confirmNewPasswordLabel")}
        value={values.confirmNewPassword}
        onChange={(event) =>
          setValues((prev) => ({
            ...prev,
            confirmNewPassword: event.target.value,
          }))
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t("auth.togglePasswordVisibility")}
                onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                edge="end"
              >
                {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {errorKey ? <Typography color="error">{t(errorKey)}</Typography> : null}

      {isSuccess ? (
        <Alert severity="success">{t("auth.changePasswordSuccess")}</Alert>
      ) : null}

      <Button type="submit" variant="contained" disabled={isSubmitting}>
        {isSubmitting ? t("common.loading") : t("auth.changePasswordButton")}
      </Button>
    </Stack>
  );
}

export default ChangePasswordForm;
